import React, {
  useMemo,
  useState,
  useContext,
  createContext,
  useCallback,
  useEffect,
} from "react";
import { Link, useRouteMatch } from "react-router-dom";
import styled, { StyledComponent } from "styled-components";
import { useTheme } from "@material-ui/styles";
import { useUniqueId } from "storybook/utils/use-unique-id";
import {
  TabsBtnSoft,
  TabsBtnHeavy,
  TabsBtnContained,
  StyledTabsHeadDefault,
  StyledTabTextDefault,
} from "./theme/default";
import {
  StyledTabTextLegacy,
  StyledTabsHeadLegacy,
  StyledTabsButtonLegacy,
} from "./theme/legacy";
import {
  StyledTabTextFuture,
  StyledTabsHeadFuture,
  StyledTabsButtonFuture,
} from "./theme/future";

import {
  TabsProps,
  TabsHeadProps,
  TabsButtonProps,
  TabsPanelProps,
  TabsThemeType,
  TabButtonType,
  TabsContextType,
  Variant,
} from "./types";
import { TextContainer } from "./styles";
import { ComponentThemeProvider } from "storybook/context/component-theme";

type VariantMap = Record<Variant, StyledComponentMap>;

const tabsButtonMap: VariantMap = {
  contained: {
    default: TabsBtnContained,
    legacy: StyledTabsButtonLegacy,
    future: StyledTabsButtonFuture,
  },
  "heavy-underline": {
    default: TabsBtnHeavy,
    legacy: StyledTabsButtonLegacy,
    future: StyledTabsButtonFuture,
  },
  "soft-underline": {
    default: TabsBtnSoft,
    legacy: StyledTabsButtonLegacy,
    future: StyledTabsButtonFuture,
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StyledComponentMap = Record<TabsProps["theme"], StyledComponent<any, any>>;

const styledTabsHeadMap: StyledComponentMap = {
  default: StyledTabsHeadDefault,
  legacy: StyledTabsHeadLegacy,
  future: StyledTabsHeadFuture,
};

const styledTabTextMap: StyledComponentMap = {
  default: StyledTabTextDefault,
  legacy: StyledTabTextLegacy,
  future: StyledTabTextFuture,
};

const StyledTabsPanel = styled.div`
  > :not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const TabContext = createContext({} as TabsContextType);

export const useTabContext = (): TabsContextType => useContext(TabContext);

function TabsHead({ children }: TabsHeadProps): JSX.Element {
  const theme: TabsThemeType = useTheme();
  const StyledTabsHead = styledTabsHeadMap[theme.name];
  const { mobileStackTabs, variant, height } = useTabContext();

  return (
    <StyledTabsHead
      role="tablist"
      $height={height}
      $variant={variant}
      $mobileStackTabs={mobileStackTabs}
      as={styledTabTextMap[theme.name]}
    >
      {children}
    </StyledTabsHead>
  );
}

function TabsButton({
  id,
  route,
  disabled,
  children,
  onClick,
  onBlur,
  className,
  suppressSpaceClick,
  maxWidth = "100%",
}: TabsButtonProps): JSX.Element {
  const theme: TabsThemeType = useTheme();
  const { variant, activeTabId, instanceId, setActiveTabId, mobileStackTabs } =
    useTabContext();
  const isMatch = useRouteMatch(route || "");
  const isValidMatch = Boolean(route && isMatch && isMatch.isExact);

  const TabsButton = useMemo(
    () => tabsButtonMap[variant][theme.name],
    [variant, theme.name]
  );

  useEffect(() => {
    if (route && isValidMatch) {
      setActiveTabId(id);
    }
  }, [route, id, isValidMatch, setActiveTabId]);

  const handleClick = useCallback(() => {
    if (!disabled) {
      setActiveTabId(id);

      if (onClick) {
        onClick();
      }
    }
  }, [disabled, id, setActiveTabId, onClick]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      let newTab: TabButtonType | null = null;

      if (event.key === "ArrowRight") {
        newTab = event.currentTarget.nextElementSibling as TabButtonType;
      } else if (event.key === "ArrowLeft") {
        newTab = event.currentTarget.previousElementSibling as TabButtonType;
      } else if (
        (!route && event.key === "Enter") ||
        (event.key === " " && !suppressSpaceClick)
      ) {
        handleClick();
        event.preventDefault();
      }

      if (newTab && newTab.getAttribute("role") === "tab") {
        newTab.focus();
      }
    },
    [handleClick, route, suppressSpaceClick]
  );

  const isActive = activeTabId === id;
  const tabId = `${instanceId}-${id}`;

  const sharedTabProps = {
    role: "tab",
    id: `tab-${tabId}`,
    onKeyDown: handleKeyDown,
    "aria-selected": isActive,
    "aria-controls": `tabpanel-${tabId}`,
    tabIndex: disabled ? -1 : 0,
    ...(typeof children === "string" ? { title: children } : {}),
  };

  let extraTabProps: Record<string, unknown>;
  if (route) {
    extraTabProps = {
      as: Link,
      to: {
        pathname: route,
        search: window.location.search,
      },
    };
  } else {
    extraTabProps = {
      onClick: handleClick,
    };
  }

  return (
    <TabsButton
      onBlur={onBlur}
      $active={isActive}
      $disabled={disabled}
      $maxWidth={maxWidth}
      className={className}
      data-id={`tab-${id}`}
      data-testid="Tabs__Button"
      $mobileStackTabs={mobileStackTabs}
      {...sharedTabProps}
      {...extraTabProps}
    >
      <TextContainer>{children}</TextContainer>
    </TabsButton>
  );
}

function TabsPanel({ children, id }: TabsPanelProps): JSX.Element | null {
  const { activeTabId, instanceId } = useTabContext();
  const tabId = `${instanceId}-${id}`;

  if (activeTabId !== id) {
    return null;
  }

  return (
    <StyledTabsPanel
      tabIndex={0}
      role="tabpanel"
      id={`tabpanel-${tabId}`}
      data-testid="Tabs__Panel"
      aria-labelledby={`tab-${tabId}`}
    >
      {children}
    </StyledTabsPanel>
  );
}

function Tabs({
  theme,
  height,
  children,
  activeId,
  initialActiveId,
  variant = "contained",
  mobileStackTabs = true,
  onTabChange,
}: TabsProps): JSX.Element {
  const [activeTabId, setActiveTabId] = useState<string>(initialActiveId);
  const instanceId = useUniqueId("Tabs");

  useEffect(() => {
    if (activeTabId !== activeId && activeId) {
      setActiveTabId(activeId);
    }
  }, [activeId, activeTabId]);

  const handleActiveTabStateChange = useCallback(
    (tabId: string) => {
      setActiveTabId(tabId);

      if (onTabChange) {
        onTabChange(tabId);
      }
    },
    [onTabChange]
  );

  const contextProps = useMemo(
    () => ({
      height,
      variant,
      instanceId,
      activeTabId,
      setActiveTabId: handleActiveTabStateChange,
      mobileStackTabs,
    }),
    [
      variant,
      activeTabId,
      instanceId,
      mobileStackTabs,
      height,
      handleActiveTabStateChange,
    ]
  );

  return (
    <ComponentThemeProvider theme={theme}>
      <TabContext.Provider value={contextProps}>{children}</TabContext.Provider>
    </ComponentThemeProvider>
  );
}

TabsButton.displayName = "Tabs.Button";
TabsHead.displayName = "Tabs.Head";
TabsPanel.displayName = "Tabs.Panel";

Tabs.Button = TabsButton;
Tabs.Head = TabsHead;
Tabs.Panel = TabsPanel;

export { Tabs };
export type { TabsProps };
